/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {cloneElement, useEffect, useMemo, useState} from 'react';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {TertiaryIconButton} from '@ion/dls/src/components/buttons/TertiaryIconButton';
import {MobileSideSheetArrow} from '@ion/dls/src/components/sidesheet/MobileSideSheetArrow';
import {CreateGeoFenceIcon} from '@ion/dls/src/icons/CreateGeoFenceIcon';
import {MapViewIcon} from '@ion/dls/src/icons/MapViewIcon';
import {BasemapLayerTool, MapTools} from '../types/types';
import BasemapGalleryTool from './BasemapGalleryTool';
import CreateGeofencesToolFactory from './CreateGeofencesTool';

interface SimpleMapControlsBottomSheetProps extends GlobalGroupProps {
  bottomSheetProps?: {
    mobileMapControlsOpen?: boolean;
    closeMapControlsSheet?: () => void;
    onMapViewClick?: () => void;
  };
  isShowBottomSheet?: boolean;
  onCloseBottomSheet?: () => void;
  map: __esri.Map;
  isGeofence?: boolean;
  createOSMBasemap: Function;
  geofenceSave?: Function;
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  fullHeight?: boolean;
  tools?: BasemapLayerTool[];
  view: __esri.MapView,
  basemapLayerRef: __esri.Layer;
  activeTool?: MapTools;
  setActiveTool?: Function;
  currentAccountId?: number;
}

const useStyles = makeStyles(theme => ({
  // This allows for the user to click outside of the
  // sheet to manipulate their fence while keeping the bottom
  // sheet open
  bottomDrawerRoot : {
    top : '0px'
  },
  bottomDrawerRootFence : {
    top : '100vh !important'
  },
  bottomDrawerPaper : {
    borderRadius : '10px 10px 0 0',
    bottom       : (props: {
      bottomSheetProps: any;
    }) => props?.bottomSheetProps?.fullHeight ? '90px' : null
  },
  bottomDrawerContent : {
    padding   : '23px',
    height    : 'auto',
    maxHeight : 'calc(100vh - 75px)'
  },
  expandBottomDrawerContent : {
    padding   : '12px',
    maxHeight : 'calc(100vh - 75px)'
  },
  typography : {
    padding : '8px 5px 0 5px'
  }
}));

/**
 * @SimpleMapControlsBottomSheet
 */
export const SimpleMapControlsBottomSheet = ({
  bottomSheetProps,
  isShowBottomSheet,
  onCloseBottomSheet,
  map,
  tools,
  view,
  globalGroup,
  accountMap,
  createOSMBasemap,
  basemapLayerRef,
  activeTool,
  setActiveTool,
  currentAccountId,
  editGeofenceGraphic,
  isGeofence = false
}: SimpleMapControlsBottomSheetProps) => {
  const classes = useStyles({bottomSheetProps});

  const [selectedTool, setSelectedTool] = useState(null);
  const [isGeofenceContentsOpen, setIsGeofenceContentsOpen] = useState(false);
  const [reinitialize, setReinitialize] = useState(false);

  const isGeofenceSelected = useMemo(() => selectedTool?.id === MapTools.createGeoFence, [selectedTool]);

  useEffect(() => {
    !reinitialize && setReinitialize(true);
    setSelectedTool(null);
    setIsGeofenceContentsOpen(false);
    onCloseBottomSheet();
  }, [currentAccountId]);

  useEffect(() => {
    if (!isShowBottomSheet && !isGeofenceContentsOpen) {
      setSelectedTool(null);
    }
  }, [isShowBottomSheet]);

  useEffect(() => {
    if (editGeofenceGraphic) {
      const geofenceTool = tools?.filter(tool => tool.id === MapTools.createGeoFence)[0];
      setSelectedTool(geofenceTool);
    } else {
      setSelectedTool(null);
    }
  }, [editGeofenceGraphic]);

  const mapControlFactories = useMemo(() => ({
    [MapTools.basemapGallery] : (
      <TertiaryIconButton label={translate('maps:controls.view')}
        icon={
          <MapViewIcon />
        }
        pressedEffect />
    ),
    [MapTools.createGeoFence] : (
      <TertiaryIconButton label={translate('maps:controls.createGeoFence')}
        icon={
          <CreateGeoFenceIcon />
        }
        pressedEffect />
    )
  }), []);

  const mapControls = useMemo(() => {
    return tools.map(tool => {
      const toolFactory = mapControlFactories[tool.id];
      if (toolFactory) {
        return cloneElement(toolFactory, {
          onClick : () => {
            setSelectedTool(tool);
          }
        });
      }
    });
  }, [mapControlFactories, tools]);

  const mapToolsFactories = useMemo(() => ({
    [MapTools.basemapGallery] : (args) => {
      const {
        tool : {
          props : {
            onBasemapChanged
          }
        },
        map,
        view,
        activeTool,
        setActiveTool,
        setSelectedTool,
        createOSMBasemap
      } = args;
      return (
        <BasemapGalleryTool map={map}
          view={view}
          createOSMBasemap={createOSMBasemap}
          onBasemapChanged={onBasemapChanged}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          setSelectedTool={setSelectedTool}
          isOpenFromBottomSheet />
      );
    },
    [MapTools.createGeoFence] : (args) => {
      const {
        tool: {
          position,
          props: {
            useHereApi,
            handleViewAllClick,
            accountMap,
            globalGroup,
            geofenceSave,
            savingGeofence,
            toggleOpenClose,
            createPopupStyle,
            isAdminGeofence,
            currentAccountId,
            isShowViewAllOption,
            editGeofenceGraphic,
            setEditGeofenceGraphic,
            open
          }
        },
        index,
        view,
        map,
        activeTool,
        setActiveTool
      } = args;
      return (
        <CreateGeofencesToolFactory view={view}
          useHereApi={useHereApi}
          handleViewAllClick={handleViewAllClick}
          geofenceSave={geofenceSave}
          savingGeofence={savingGeofence}
          position={position}
          open={!!open}
          reinitialize={reinitialize}
          setReinitialize={setReinitialize}
          accountMap={accountMap}
          globalGroup={globalGroup}
          currentAccountId={currentAccountId}
          createPopupStyle={createPopupStyle}
          editGeofenceGraphic={editGeofenceGraphic}
          isAdminGeofence={isAdminGeofence}
          isShowViewAllOption={isShowViewAllOption}
          setEditGeofenceGraphic={setEditGeofenceGraphic}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          toggleOpenClose={toggleOpenClose}
          map={map}
          setIsGeofenceContentsOpen={setIsGeofenceContentsOpen}
          onCloseBottomSheet={onCloseBottomSheet}
          setSelectedTool={setSelectedTool}
          isOpenFromBottomSheet />
      );
    }
  }), [setIsGeofenceContentsOpen, currentAccountId, reinitialize]);

  const mapTools = useMemo(() => {
    return mapToolsFactories?.[selectedTool?.id]?.({
      tool : selectedTool,
      map,
      view,
      createOSMBasemap,
      basemapLayerRef,
      activeTool,
      setActiveTool,
      tools,
      setSelectedTool
    });
  }, [selectedTool, currentAccountId, reinitialize, createOSMBasemap]);

  return (
    <Drawer keepMounted
      anchor='bottom'
      open={isShowBottomSheet}
      onClose={onCloseBottomSheet}
      ModalProps={{
        hideBackdrop : isGeofence || isGeofenceContentsOpen
      }}
      classes={{
        root  : (isGeofence || isGeofenceContentsOpen) ? classes.bottomDrawerRootFence : classes.bottomDrawerRoot,
        paper : classes.bottomDrawerPaper
      }}>
      <MobileSideSheetArrow handleTapDown={onCloseBottomSheet}
        mapControlBottomSheet />
      <div className={
        ((selectedTool?.id === MapTools.basemapGallery))
          ? classes.expandBottomDrawerContent
          : classes.bottomDrawerContent
      }>
        {!selectedTool && (
          <>
            <Typography component='h1' variant='h4' className={classes.typography}>
              {translate('maps:controls.header')}
            </Typography>
            <div>
              {mapControls}
            </div>
          </>
        )}
        {selectedTool && (
          <div>
            {mapTools}
          </div>
        )}
      </div>
    </Drawer>
  );
};
export default SimpleMapControlsBottomSheet;
